/**
 * MandatorySignaturesBlocker — Fullscreen blocker shown right after the
 * user logs in if there are documents pending signature.
 *
 * Behaviour:
 *  - Polls `/api/employee/pending-signatures` on mount + after each sign
 *  - While pending items > 0, locks the entire app behind a modal that
 *    cannot be dismissed (Esc/backdrop disabled)
 *  - Sequentially opens UniversalSignDialog for each pending doc
 *  - When the user signs ALL pending docs, the blocker self-closes
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import UniversalSignDialog from "./UniversalSignDialog";
import { Loader2, FileSignature, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const ICON_BY_KIND = {
  contract: "📄",
  epi: "🦺",
  badge: "🪪",
  vt: "🚌",
  nao_adesao: "🩺",
  termino_contrato: "📤",
  termino_comunicado: "📢",
  termino_trct: "📘",
  termino_protocolo: "📗",
  vacation_notice: "💰",
  vacation_advance: "🏖️",
  kit_integracao_matriz: "🏢",
  kit_integracao_filial: "🏬",
  kit_lgpd: "🔒",
  kit_ordem_servico: "📋",
  kit_ficha_registro: "📑",
};

export default function MandatorySignaturesBlocker({ enabled = true }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);

  const refresh = useCallback(async () => {
    if (!token || !enabled) return;
    try {
      const r = await axios.get(`${API}/employee/pending-signatures`, {
        params: { token }, timeout: 15000,
      });
      const arr = r.data?.items || [];
      setItems(arr);
      // Auto-open first when no dialog is open
      if (arr.length > 0 && !current) {
        setCurrent(arr[0]);
      }
      // If user signed last one, close the dialog
      if (arr.length === 0) {
        setCurrent(null);
      }
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [token, enabled, current]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!enabled || loading || items.length === 0) return null;

  return (
    <>
      {/* Full-screen blocker overlay */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="fixed inset-0 z-40 bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="mandatory-signatures-blocker">
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
              <FileSignature className="w-6 h-6 text-sky-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-slate-900 text-lg">
                Documentos pendentes
              </h2>
              <p className="text-xs text-slate-500">
                Você tem {items.length} documento(s) que precisam ser assinados antes de continuar.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 max-h-60 overflow-y-auto">
            {items.map((it, idx) => (
              <div key={`${it.kind}-${it.id}`}
                className={`flex items-center gap-3 p-3 ${
                  idx > 0 ? "border-t border-slate-100" : ""
                } ${current?.id === it.id ? "bg-sky-50" : ""}`}>
                <div className="text-2xl">
                  {ICON_BY_KIND[it.kind] || "📃"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">
                    {it.label}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Aguardando sua assinatura
                  </div>
                </div>
                {current?.id === it.id && (
                  <ArrowRight className="w-4 h-4 text-sky-600 animate-pulse" />
                )}
              </div>
            ))}
          </div>

          <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900">
            <strong>Por favor assine cada documento</strong> para liberar o acesso ao painel. Você pode assinar via SMS ou e-mail.
          </div>

          {!current && items.length > 0 && (
            <Button onClick={() => setCurrent(items[0])}
              className="w-full bg-sky-700 hover:bg-sky-800 text-white"
              data-testid="mandatory-start-btn">
              <FileSignature className="w-4 h-4 mr-2" />
              Começar com {items[0].label}
            </Button>
          )}

          {current && (
            <div className="text-center">
              <Loader2 className="w-4 h-4 inline-block animate-spin text-slate-400 mr-2" />
              <span className="text-xs text-slate-500">
                Abrindo {current.label}…
              </span>
            </div>
          )}
        </motion.div>
      </motion.div>

      {current && (
        <UniversalSignDialog
          kind={current.kind} id={current.id}
          variant={current.variant}
          open={true} blocking
          onClose={() => {
            // After signing one, refresh and move to the next.
            setCurrent(null);
            refresh();
          }}
          onSigned={() => {
            // Trigger refresh — closing happens on user clicking "Continuar"
          }}
        />
      )}
    </>
  );
}
